import type { BookingMode, SaleClose, SaleEndAnchor } from "./types";

/** The modes a partner may pick. `interval` is deferred to v2.1: a mode the product cannot
 *  honour is worse than a missing one, so authoring never offers it. */
export const authorableBookingModes = ["session", "entry"] as const;

export type AuthorableBookingMode = (typeof authorableBookingModes)[number];

/** An activity that somehow carries `interval` still has to be editable, and v2 has no screen
 *  that can express it — so it opens as a pevný termín. */
export function toAuthorableBookingMode(mode: BookingMode): AuthorableBookingMode {
  return mode === "interval" ? "session" : mode;
}

/** Mirrors the API's `SALE_CLOSES_AFTER_START` refusal on a template that
 *  confirms by hand. Counted from the end, even a negative offset is called
 *  after the start: the API does not know the length of a termín the template
 *  has not been applied to. */
export function closesSaleAfterStart({ offsetMinutes, anchor }: SaleClose): boolean {
  return anchor === "end" || offsetMinutes > 0;
}

/** Counted from the end, only a positive offset is past it. Counted from the
 *  start it depends on the termín's length, which a template that sets none
 *  leaves to each termín — there the slot-level rule is the check. */
export function closesSaleAfterEnd(
  { offsetMinutes, anchor }: SaleClose,
  durationMinutes: number | null | undefined,
): boolean {
  if (anchor === "end") return offsetMinutes > 0;
  return typeof durationMinutes === "number" && offsetMinutes > durationMinutes;
}

/** The API caps a sale at the end of the termín anyway, so a stored positive
 *  offset from the end is shown in the šablona form as the end itself. */
export function clampedToSlotEnd(close: SaleClose): SaleClose {
  return close.anchor === "end" && close.offsetMinutes > 0 ? { ...close, offsetMinutes: 0 } : close;
}

export type SaleCloseUnit = "minutes" | "hours";
export type SaleCloseDirection = "before" | "after";

export interface SaleCloseParts {
  amount: number;
  unit: SaleCloseUnit;
  direction: SaleCloseDirection;
  anchor: SaleEndAnchor;
}

const MINUTES_IN_HOUR = 60;

/** Zero is "0 minutes before", the direction that does not matter. */
export function saleCloseParts({ offsetMinutes, anchor }: SaleClose): SaleCloseParts {
  const minutes = Math.abs(offsetMinutes);
  const isWholeHours = minutes > 0 && minutes % MINUTES_IN_HOUR === 0;
  return {
    amount: isWholeHours ? minutes / MINUTES_IN_HOUR : minutes,
    unit: isWholeHours ? "hours" : "minutes",
    direction: offsetMinutes > 0 ? "after" : "before",
    anchor,
  };
}

export function saleCloseFromParts({ amount, unit, direction, anchor }: SaleCloseParts): SaleClose {
  const minutes = unit === "hours" ? amount * MINUTES_IN_HOUR : amount;
  const isBefore = direction === "before" && minutes > 0;
  return { offsetMinutes: isBefore ? -minutes : minutes, anchor };
}

export function saleCloseOf(template: {
  saleEndOffsetMinutes: number | null;
  saleEndAnchor: SaleEndAnchor | null;
}): SaleClose | null {
  const { saleEndOffsetMinutes, saleEndAnchor } = template;
  if (saleEndOffsetMinutes === null || saleEndAnchor === null) return null;
  return { offsetMinutes: saleEndOffsetMinutes, anchor: saleEndAnchor };
}
